import { useRef, type ClipboardEvent, type KeyboardEvent } from "react";

export interface OtpInputProps {
  length: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: () => void;
}

/**
 * Reemplazo de `<PinInput>` de Mantine · N casillas de un dígito con
 * auto-avance (escribir salta a la siguiente, Backspace en una vacía
 * salta a la anterior) y soporte de pegado (`Ctrl+V` de un código completo
 * lo reparte en las casillas). Promovido desde `login-form.tsx` (única app
 * inicial) a átomo compartido porque la página Seguridad (P36) también
 * necesita un OTP de 6 dígitos.
 */
export function OtpInput({ length, value, onChange, onComplete }: OtpInputProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? "");

  function setDigitAt(index: number, digit: string) {
    const next = digits.slice();
    next[index] = digit;
    const joined = next.join("");
    onChange(joined);
    if (joined.length === length && next.every((d) => d !== "")) onComplete?.();
  }

  function onInputChange(index: number, raw: string) {
    const digit = raw.replace(/\D/g, "").slice(-1);
    setDigitAt(index, digit);
    if (digit && index < length - 1) inputsRef.current[index + 1]?.focus();
  }

  function onKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    const focusIndex = Math.min(pasted.length, length - 1);
    inputsRef.current[focusIndex]?.focus();
    if (pasted.length === length) onComplete?.();
  }

  return (
    <div className="flex gap-2" role="group" aria-label="Código de verificación">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          autoFocus={i === 0}
          value={d}
          onChange={(e) => onInputChange(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onPaste={onPaste}
          aria-label={`Dígito ${i + 1}`}
          className="size-11 rounded-md border border-input bg-background text-center text-lg font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-multiva-600/30 focus:border-multiva-600"
        />
      ))}
    </div>
  );
}
